import { Link } from 'react-router-dom';
import { ProjectSwitcher, type ProjectSwitcherEintrag } from './ProjectSwitcher';
import { Aufklappmenue } from './Aufklappmenue';
import { IconSuche, IconZustand, IconHilfe } from '../Icons';
import './rahmen.css';

/**
 * Globale Kopfzeile (Auftrag Abschnitt 4):
 *
 *   [MEPLORA-Logo] [Projekt] [Suche]
 *                                        [Status] [Hilfe] [Benutzer]
 *
 * Die Marke ist seit 19.09.2026 MEPLORA (Plan
 * `docs/PLAN-2026-09-19-branding-meplora.md`). An dieser Stelle stand bis
 * dahin eine GETIPPTE Textmarke "TEUBER+VIEL / TGA Revit Export Platform".
 * Sie ist ersatzlos durch das gelieferte Logo-SVG ersetzt, und zwar aus
 * drei Gründen, die alle aus der CI kommen und keine Geschmacksfragen sind:
 *
 *   - "Do not recreate the logo by typing the name"
 *     (`06_TYPOGRAPHY/FONT_SETUP.txt`). Die Wortmarke MEPLORA ist festes
 *     Artwork, kein Text in der Seitenschrift. Deshalb ein <img>, kein <span>.
 *   - Das Logo besteht NUR aus Zeichen + Wortmarke. "MEPLORA Technologies
 *     GmbH" gehört ausdrücklich NICHT hinein (`00_MASTER_REFERENCE/
 *     MASTER_LOCK.txt`); der Firmenname steht im PDF-Fuß, nicht hier. Ein
 *     Untertitel unter dem Logo wäre ebenfalls ein verbotener Zusatz
 *     ("combined with a subtitle") - deshalb ist auch die Produktzeile weg.
 *   - Auf dem dunklen Kopf gilt die weiße Fassung, nicht die rote.
 *
 * Die Maße stehen in `rahmen.css` (.rahmen-kopf-marke). Seit Festlegung F12
 * vom 18.09.2026 sind es 160 px statt 188 px Bildbreite - 15 % kleiner, und
 * damit mit 153,6 px Zeichenbreite unter der CI-Mindestgröße von 180 px. Das
 * ist so angeordnet; nachzulesen bei `Marke.LogoVerkleinerungF12`.
 *
 * Drei Entscheidungen, die der Auftrag offen lässt:
 *
 * 1. STATUS ist kein Glockensymbol mit erfundenen Meldungen, sondern der
 *    Weg auf die vorhandene Zustandsseite (`/`): Aufträge, Agenten,
 *    Maschinen. Eine Benachrichtigungsglocke bräuchte einen Endpunkt für
 *    ungelesene Meldungen, den es nicht gibt - sie wäre eine Fake-Funktion
 *    (Abschnitt 13).
 * 2. HILFE führt auf die vorhandenen `Modellvorgaben` - die Seite, die
 *    erklärt, wie das Modell auszusehen hat. Das ist die einzige
 *    Hilfeseite, die es gibt; eine zweite wird nicht erfunden.
 * 3. Der Projektname steht GENAU EINMAL in der Oberfläche, nämlich hier
 *    (Abschnitt 4: "Projektname nicht mehrfach in verschiedenen Menüs
 *    darstellen"). Die frühere Wiederholung in der Projektzeile ist
 *    entfallen.
 *
 * "Abmelden" liegt im Benutzermenü und nicht mehr offen in der Kopfzeile
 * (Abschnitt 4).
 */
export interface TopHeaderProps {
  /** Aktuell offenes Projekt. */
  projekt?: ProjectSwitcherEintrag | null;
  /** Wählbare Projekte; ein oder keines heißt: nur Anzeige. */
  projekte?: ProjectSwitcherEintrag[];
  onProjektWechsel?: (id: string) => void;

  /** Öffnet die Befehlspalette. Wird vom `AppShell` gesetzt. */
  onSucheOeffnen?: () => void;

  /** Anmeldename des Benutzers (E-Mail), falls angemeldet. */
  benutzerName?: string | null;
  /** Anzeigename, falls bekannt - sonst wird der Anmeldename verwendet. */
  benutzerAnzeige?: string | null;
  onAbmelden?: () => void;
  /** Ziel für "Einstellungen" im Benutzermenü; fehlt es, entfällt der Punkt. */
  einstellungenPfad?: string;
  /**
   * F312: live laufende Version, schon gekürzt (`v0.53.3`, siehe `kopfversion.ts`). Steht rechts neben „Suchen und
   * springen“; fehlt sie, entfällt die Anzeige ganz.
   */
  version?: string | null;
}

/** Kürzel aus dem Namen - "m.holler@..." wird zu "MH". */
function kuerzel(name: string | null | undefined): string {
  if (!name) return '?';
  const teile = name.split('@')[0].split(/[.\-_\s]+/).filter(Boolean);
  if (teile.length === 0) return '?';
  return (teile[0][0] + (teile[1]?.[0] ?? '')).toUpperCase();
}

export function TopHeader({
  projekt = null,
  projekte = [],
  onProjektWechsel,
  onSucheOeffnen,
  benutzerName,
  benutzerAnzeige,
  onAbmelden,
  einstellungenPfad,
  version = null,
}: TopHeaderProps) {
  const tastenkuerzel = typeof navigator !== 'undefined'
    && /Mac|iPhone|iPad/.test(navigator.platform ?? '')
    ? '⌘K' : 'Strg+K';

  return (
    <header className="rahmen-kopf">
      {/* Die Marke führt auf die Startseite - erwartbar und spart einen
          eigenen "Zur Übersicht"-Punkt. */}
      <Link to="/" className="rahmen-kopf-marke" aria-label="MEPLORA - zur Startseite">
        <img
          src="/marke/MEPLORA_Primary_White.svg"
          alt="MEPLORA"
          className="rahmen-kopf-logo"
        />
      </Link>

      <div className="rahmen-kopf-trenner" aria-hidden="true" />

      <ProjectSwitcher
        projekt={projekt}
        projekte={projekte}
        onWechsel={onProjektWechsel}
      />

      {/* Kein `input`, sondern ein Knopf: Getippt wird in der Befehlspalette,
          und ein Feld, das bei jedem Zeichen ein Overlay öffnet, führt zu
          zwei Eingabefeldern mit demselben Text. */}
      <button
        type="button"
        className="rahmen-kopf-suche"
        onClick={onSucheOeffnen}
        aria-label={`Suchen und springen (${tastenkuerzel})`}
      >
        <IconSuche size={15} />
        <span className="rahmen-kopf-suche-text">Suchen und springen</span>
        <kbd className="rahmen-kopf-kuerzel">{tastenkuerzel}</kbd>
      </button>

      {/* F312: die live laufende Version, dezent hinter der Suche. Kein Link und kein Knopf - reine Anzeige;
          die ausführliche Angabe (Bauzeit, Commit) steht weiter in der Fußzeile der Startseite. */}
      {version && (
        <span className="rahmen-kopf-version" data-testid="kopf-version" title={`Live-Version ${version}`}>
          {version}
        </span>
      )}

      <div className="rahmen-kopf-rechts">
        <Link
          to="/"
          className="rahmen-kopf-knopf"
          aria-label="Systemzustand: Aufträge, Agenten, Maschinen"
          title="Systemzustand"
        >
          <IconZustand size={17} />
        </Link>

        <Link
          to="/modellvorgaben"
          className="rahmen-kopf-knopf"
          aria-label="Hilfe: Modellvorgaben"
          title="Hilfe – Modellvorgaben"
        >
          <IconHilfe size={17} />
        </Link>

        <Aufklappmenue
          ausloeserBeschriftung={benutzerName ? `Benutzer ${benutzerName}` : 'Benutzer'}
          ausloeserKlasse="rahmen-kopf-benutzer-knopf"
          ausloeser={(
            <>
              <span className="benutzer-zeichen" aria-hidden="true">
                {kuerzel(benutzerAnzeige ?? benutzerName)}
              </span>
              <span className="aufklappmenue-pfeil" aria-hidden="true">▾</span>
            </>
          )}
        >
          <div className="aufklappmenue-kopf">
            <div className="aufklappmenue-kopf-name">
              {benutzerAnzeige ?? benutzerName ?? 'Nicht angemeldet'}
            </div>
            {benutzerName && benutzerAnzeige !== benutzerName && (
              <div className="aufklappmenue-kopf-zeile">{benutzerName}</div>
            )}
          </div>

          {einstellungenPfad && (
            <Link
              to={einstellungenPfad}
              role="menuitem"
              className="aufklappmenue-punkt"
            >
              Einstellungen
            </Link>
          )}

          <button
            type="button"
            role="menuitem"
            className="aufklappmenue-punkt"
            onClick={onAbmelden}
          >
            Abmelden
          </button>
        </Aufklappmenue>
      </div>
    </header>
  );
}
